import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  formatPersistentEditMetadata,
  formatPersistentEditValue,
  type OverrideKind,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  AI_SPREADSHEET_OVERRIDE_REVIEW_REASON,
  type IAiSpreadsheetCellLocation,
  type IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";
import { OverrideRowActions } from "./override-row-actions";
import {
  BoldValue,
  CellLocationLink,
  EditCard,
  EditValueChange,
} from "./shared";

interface OverrideRowProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  kind: OverrideKind;
  canResolve: boolean | null;
  onFocus?: () => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onResolved?: () => void;
  // Fired on a successful discard/accept (which advance the server workbook
  // version) so the session undo/redo stack can be reset.
  onWorkbookMutated?: () => void;
}

export const OverrideRow = ({
  override,
  businessId,
  runId,
  kind,
  canResolve,
  onFocus,
  onGoToCell,
  onResolved,
  onWorkbookMutated,
}: OverrideRowProps) => {
  const isAmbiguous =
    override.review_reason === AI_SPREADSHEET_OVERRIDE_REVIEW_REASON.ambiguous;
  // In the list the cards sit under a heading that names their status, so only
  // the ones that break the pattern need to repeat it.
  const isListCard = !!onFocus;

  const statusBadge =
    kind === "dropped" ? (
      <Badge variant="secondary" size="sm">
        Dropped
      </Badge>
    ) : kind === "needs_review" ? (
      <Badge variant="secondary" color="warning" size="sm">
        Needs review
      </Badge>
    ) : !isListCard ? (
      <Badge variant="secondary" color="success" size="sm">
        Applied
      </Badge>
    ) : null;

  const openEditButton = isListCard ? (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label="Open edit history"
      onClick={(event) => {
        event.stopPropagation();
        onFocus?.();
      }}
    >
      <CaretRightIcon className="text-subtle" />
    </Button>
  ) : null;

  const note =
    kind === "dropped" ? (
      "This row wasn't on the latest run, so the edit couldn't re-apply."
    ) : isAmbiguous ? (
      "This edit matched more than one row. Pick the row it belongs to."
    ) : kind === "needs_review" ? (
      <>
        The payroll logic now generates{" "}
        <BoldValue>
          {formatPersistentEditValue(override.generated_value)}
        </BoldValue>{" "}
        for this cell.
      </>
    ) : null;

  const metadata = formatPersistentEditMetadata(override);

  const actions = (
    <OverrideRowActions
      override={override}
      businessId={businessId}
      runId={runId}
      kind={kind}
      canResolve={canResolve}
      isListCard={isListCard}
      onFocus={onFocus}
      onResolved={onResolved}
      onWorkbookMutated={onWorkbookMutated}
    />
  );

  const body = (
    <>
      <Stack
        direction="row"
        gap={2}
        align="center"
        justify="between"
        className="w-full min-w-0"
      >
        {override.cell ? (
          <CellLocationLink cell={override.cell} onGoToCell={onGoToCell} />
        ) : (
          <Text variant="label" tone="muted">
            {isAmbiguous ? "Matched multiple rows" : "Row not on this run"}
          </Text>
        )}
        <Stack direction="row" gap={1} align="center">
          {statusBadge}
          {openEditButton}
        </Stack>
      </Stack>

      <EditValueChange
        from={override.previous_value}
        to={override.value?.computed_value}
      />

      {note ? <Text tone="muted">{note}</Text> : null}

      <Text
        variant="caption"
        tone="muted"
        className="truncate"
        title={metadata}
      >
        {metadata}
      </Text>
    </>
  );

  const content = (
    <>
      {body}
      {actions}
      {canResolve === false ? (
        <Text variant="caption" tone="muted">
          You have view-only access to this edit.
        </Text>
      ) : null}
    </>
  );

  // Opened on its own, the card is already the page — no shell, no click target.
  if (!isListCard) {
    return (
      <Stack gap={2} align="stretch" className="w-full min-w-0">
        {content}
      </Stack>
    );
  }

  return (
    <EditCard onClick={onFocus} data-persistent-edit-card>
      {content}
    </EditCard>
  );
};
